import { useEffect, useState } from "react";
import { useAppIdToken } from "../auth/useAppIdToken";

// Mirrors api/src/lib/newsFeeds.ts.
export type NewsCategory =
  | "Models"
  | "Microsoft & Copilot"
  | "Tools & Apps"
  | "Business"
  | "Policy & Safety"
  | "Hardware"
  | "Real Estate & Construction";

export type NewsItem = {
  id: string;
  title: string;
  url: string;
  source: string;
  domain: string;
  kind: "vendor" | "press";
  publishedAt: string;
  summary: string;
  category: NewsCategory;
  image?: string;
};

export type NewsPayload = {
  updatedAt: string;
  sources: string[];
  /** realEstate: what the news means for a homebuilder (newer pulls only). */
  brief: { headline: string; bullets: string[]; realEstate?: string[] } | null;
  items: NewsItem[];
};

export function useAINews() {
  const getIdToken = useAppIdToken();
  const [data, setData] = useState<NewsPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch("/api/news", { headers: { "X-Id-Token": await getIdToken() } });
        if (!res.ok) {
          const body = await res.json().catch(() => null);
          throw new Error(body?.error ?? `AI News failed (${res.status}).`);
        }
        const payload = (await res.json()) as NewsPayload;
        if (!cancelled) setData(payload);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [getIdToken]);

  return { data, loading, error };
}
